/**
 * Community Form Dialog Component
 * Dialog for creating and editing community pricing profiles
 * Includes "Details" and "Accounts" tabs when editing existing community
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Textarea } from '@/core/components/ui/textarea';
import { Checkbox } from '@/core/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/core/components/ui/tabs';
import { useCommunitiesStore } from '../../stores/communities-store';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useActorStore } from '@/features/site-management/stores/actor-store';
import { getUserNameFromToken } from '../../utils/format-helpers';
import { LocationSelector } from '@/core/components/LocationSelector';
import { DatePicker } from '../molecules/DatePicker';
import { CommunityAccountsTab } from './CommunityAccountsTab';
import type { CommunityProfile, InsertCommunityRequest, UpdateCommunityRequest } from '../../types/communities-types';

interface CommunityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  community?: CommunityProfile | null;
  defaultActorId?: number; // Default location from parent page
}

export function CommunityFormDialog({
  open,
  onOpenChange,
  community,
  defaultActorId,
}: CommunityFormDialogProps) {
  const { token, operatorId: authOperatorId, accountId } = useAuthStore();
  const { selectedOperator } = useOperatorStore();
  const { createCommunity, updateCommunity, communities } = useCommunitiesStore();
  const { actors, isLoading: actorsLoading, loadActorTree } = useActorStore();

  // Track the newly created community (separate from the prop)
  const [createdCommunity, setCreatedCommunity] = useState<CommunityProfile | null>(null);

  // Load actors when dialog opens
  useEffect(() => {
    if (open && selectedOperator?.operatorId && !community && !createdCommunity) {
      loadActorTree(selectedOperator.operatorId.toString());
    }
  }, [open, selectedOperator?.operatorId, loadActorTree, community, createdCommunity]);

  // Reset tab to 'details' when dialog opens or closes
  useEffect(() => {
    if (open) {
      setActiveTab('details');
      setCreatedCommunity(null); // Reset created community when opening
    }
  }, [open]);


  // Use either the passed community prop or the newly created one
  const currentCommunity = community || createdCommunity;
  const isEditMode = !!currentCommunity;

  // Tab state - show tabs only in edit mode
  const [activeTab, setActiveTab] = useState<'details' | 'accounts'>('details');

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    actorId: '', // Actor/location selection
    profileName: '',
    profileDescription: '',
    usageType: '1', // Default to Public
    strategyType: '2', // Default to Total Percentage (0=Energy %, 1=Per kWh, 2=Total %)
    singleDimensionValue: '',
    startDate: '',
    expirationDate: '',
    processOrder: '0',
    profileStatus: false, // Active by default (true = 0 = Active, false = 1 = Inactive)
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Populate form when editing or set defaults for create mode
  useEffect(() => {
    if (currentCommunity && open) {
      // Get the latest version of this community from the store to avoid stale data
      const freshCommunity = communities.find(c => c.profileId === currentCommunity.profileId) || currentCommunity;


      // Map strategyType - handle both number and string enum from API
      // Database stores 0,1,2 matching frontend values
      let strategyTypeValue = '2'; // default to Total Percentage
      if (typeof freshCommunity.strategyType === 'number') {
        // Use database value directly (0,1,2)
        strategyTypeValue = Math.max(0, Math.min(2, freshCommunity.strategyType)).toString();
      } else if (typeof freshCommunity.strategyType === 'string') {
        // Map string enum names to numeric values
        const strategyMap: Record<string, string> = {
          'FixedPercentageEnergy': '0',
          'FixedCentsPerKwh': '1',
          'FixedPercentage': '2',
        };
        strategyTypeValue = strategyMap[freshCommunity.strategyType] || '2';
      }

      // Get usageType from location's machineUsage instead of saved community data
      let usageTypeValue = '1'; // Default to Public
      const selectedActor = actors.find(actor => actor.actorId === freshCommunity.actorId);
      if (selectedActor?.machineUsage) {
        // Map MachineUsage enum to numeric values
        const usageTypeMap: Record<string, string> = {
          'Public': '1',
          'SemiPublic': '2',
          'PrivateHouse': '3',
          'SharedBuilding': '4',
        };
        usageTypeValue = usageTypeMap[selectedActor.machineUsage] || '1';
      } else {
        // Fallback: use saved community data if location not found
        if (typeof freshCommunity.usageType === 'number') {
          usageTypeValue = freshCommunity.usageType.toString();
        } else if (typeof freshCommunity.usageType === 'string') {
          const usageTypeMap: Record<string, string> = {
            'Public': '1',
            'SemiPublic': '2',
            'PrivateHouse': '3',
            'SharedBuilding': '4',
          };
          usageTypeValue = usageTypeMap[freshCommunity.usageType] || '1';
        }
      }

      const newFormData = {
        actorId: freshCommunity.actorId.toString(),
        profileName: freshCommunity.profileName,
        profileDescription: freshCommunity.profileDescription,
        usageType: usageTypeValue,
        strategyType: strategyTypeValue,
        singleDimensionValue: freshCommunity.singleDimensionValue?.toString() || '',
        startDate: freshCommunity.startDate.split('T')[0], // Extract YYYY-MM-DD
        expirationDate: freshCommunity.expirationDate ? freshCommunity.expirationDate.split('T')[0] : '',
        processOrder: freshCommunity.processOrder.toString(),
        profileStatus: freshCommunity.profileStatus === 'Active' || freshCommunity.profileStatus === '1', // API returns "Active" or "Deleted" as string
      };
      setFormData(newFormData);
      setError(null);
      setValidationErrors({});
    } else if (open) {
      // Reset form for create mode with default location from parent
      // Get usageType from default location if provided
      let initialUsageType = '1'; // Default to Public
      if (defaultActorId) {
        const defaultActor = actors.find(actor => actor.actorId === defaultActorId);
        if (defaultActor?.machineUsage) {
          const usageTypeMap: Record<string, string> = {
            'Public': '1',
            'SemiPublic': '2',
            'PrivateHouse': '3',
            'SharedBuilding': '4',
          };
          initialUsageType = usageTypeMap[defaultActor.machineUsage] || '1';
        }
      }

      setFormData({
        actorId: defaultActorId ? defaultActorId.toString() : '',
        profileName: '',
        profileDescription: '',
        usageType: initialUsageType,
        strategyType: '2', // Default to Total Percentage
        singleDimensionValue: '',
        startDate: '',
        expirationDate: '',
        processOrder: '0',
        profileStatus: false, // Active = 0 by default for new communities
      });
      setError(null);
      setValidationErrors({});
    }
  }, [currentCommunity, open, defaultActorId, communities, actors]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Actor/Location is always required
    if (!formData.actorId) {
      errors.actorId = 'Location is required';
    }

    if (!formData.profileName.trim()) {
      errors.profileName = 'Profile name is required';
    } else if (formData.profileName.length > 100) {
      errors.profileName = 'Profile name must be 100 characters or less';
    }

    if (formData.profileDescription.length > 500) {
      errors.profileDescription = 'Description must be 500 characters or less';
    }

    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }

    if (formData.expirationDate && formData.startDate) {
      const start = new Date(formData.startDate);
      const expiry = new Date(formData.expirationDate);
      if (expiry <= start) {
        errors.expirationDate = 'Expiration date must be after start date';
      }
    }

    // Validate discount value - Required field
    if (!formData.singleDimensionValue || formData.singleDimensionValue.trim() === '') {
      errors.singleDimensionValue = 'Discount value is required';
    } else {
      const value = parseFloat(formData.singleDimensionValue);
      if (isNaN(value) || value < 0) {
        errors.singleDimensionValue = 'Discount value must be a positive number';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const updatedByName = getUserNameFromToken(token);

      if (isEditMode && currentCommunity) {
        // Update existing community
        // Use 0,1,2 consistently everywhere
        const updateData: UpdateCommunityRequest = {
          profileId: currentCommunity.profileId,
          actorId: parseInt(formData.actorId), // Include location in update
          profileName: formData.profileName,
          profileDescription: formData.profileDescription,
          usageType: parseInt(formData.usageType), // MachineUsage enum
          profileStatus: !formData.profileStatus ? 1 : 0, // Convert boolean to number (0=Active, 1=Inactive)
          strategyType: parseInt(formData.strategyType), // Use 0,1,2 directly
          singleDimensionValue: formData.singleDimensionValue ? parseFloat(formData.singleDimensionValue) : null,
          strategySettings: currentCommunity.strategySettings || '', // Keep existing or empty string
          startDate: formData.startDate,
          expirationDate: formData.expirationDate || null,
          processOrder: parseInt(formData.processOrder),
          updatedByName,
        };



        await updateCommunity(currentCommunity.profileId, updateData);

        toast.success(`Community "${formData.profileName}" updated successfully`);

        // Close dialog after update
        onOpenChange(false);
      } else {
        // Create new community - use selected actorId from dropdown
        // Use 0,1,2 consistently everywhere
        const createData: InsertCommunityRequest = {
          actorId: parseInt(formData.actorId),
          profileName: formData.profileName,
          profileDescription: formData.profileDescription,
          profileStatus: 0, // Set to Active by default (0=active, 1=deleted)
          usageType: parseInt(formData.usageType), // MachineUsage enum: 1=Public, 2=SemiPublic, 3=PrivateHouse, 4=SharedBuilding
          strategyType: parseInt(formData.strategyType), // Use 0,1,2 directly
          singleDimensionValue: formData.singleDimensionValue ? parseFloat(formData.singleDimensionValue) : undefined,
          strategySettings: '', // Required by backend - empty string for now
          startDate: formData.startDate,
          expirationDate: formData.expirationDate || undefined,
          processOrder: parseInt(formData.processOrder),
          updatedByName,
        };

        // Create community and get the new profileId
        const createdProfileId = await createCommunity(createData);
        toast.success(`Community "${formData.profileName}" created successfully`);

        // Find the newly created community from the refreshed list
        // Wait a moment for the store to update
        await new Promise(resolve => setTimeout(resolve, 100));
        const newCommunity = communities.find(c => c.profileId === createdProfileId);

        if (newCommunity) {
          // Store the created community locally
          setCreatedCommunity(newCommunity);
          // Switch to Accounts tab
          setActiveTab('accounts');
          // Dialog stays open - user can now manage accounts
        } else {
          // Create a minimal community object from the form data if not found in list
          const tempCommunity: CommunityProfile = {
            profileId: createdProfileId,
            appId: 0, // Default value
            accountId: 0, // Default value
            actorId: parseInt(formData.actorId),
            profileName: formData.profileName,
            profileDescription: formData.profileDescription,
            usageType: parseInt(formData.usageType),
            profileStatus: formData.profileStatus ? 'Inactive' : 'Active', // Convert boolean to string
            processOrder: parseInt(formData.processOrder),
            strategyType: parseInt(formData.strategyType),
            singleDimensionValue: parseFloat(formData.singleDimensionValue) || null,
            strategySettings: '', // Default empty string
            startDate: formData.startDate,
            expirationDate: formData.expirationDate || null,
            lastUpdated: new Date().toISOString(),
            updatedByName,
          };
          setCreatedCommunity(tempCommunity);
          // Switch to Accounts tab
          setActiveTab('accounts');
          // Dialog stays open - user can now manage accounts
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save community';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDiscountLabel = () => {
    switch (formData.strategyType) {
      case '0':
        return 'Percentage (%)'; // Energy Percentage
      case '1':
        return 'Amount (Cents per kWh)'; // Energy Amount
      case '2':
        return 'Percentage (%)'; // Total Percentage
      default:
        return 'Discount Value';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`w-full sm:max-w-2xl ${activeTab === 'accounts' ? 'p-0 flex flex-col' : 'overflow-y-auto'}`}
      >
        <div className={activeTab === 'accounts' ? 'flex-shrink-0 px-6 pt-6 pb-0' : ''}>
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <SheetTitle className="text-2xl font-semibold">{isEditMode ? 'Edit Community' : 'Add New Community'}</SheetTitle>
              {isEditMode && currentCommunity && (
                <span className="text-sm text-gray-500">
                  ID: <span className="font-bold">{currentCommunity.profileId}</span>
                </span>
              )}
            </div>
          </SheetHeader>
        </div>

          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'details' | 'accounts')} className={`w-full ${activeTab === 'accounts' ? 'flex-1 flex flex-col min-h-0 px-6 mt-6' : 'mt-6'}`}>
            <TabsList className={`grid w-full grid-cols-2 h-12 bg-gray-100 p-1 ${activeTab === 'accounts' ? 'flex-shrink-0' : ''}`}>
            <TabsTrigger
              value="details"
              className="text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Community
            </TabsTrigger>
            <TabsTrigger
              value="accounts"
              disabled={!isEditMode || !currentCommunity}
              className="text-base font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm disabled:opacity-40"
            >
              Accounts
            </TabsTrigger>
          </TabsList>

          {/* Details Tab Content */}
          <TabsContent value="details" className="mt-6">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
          <div className="flex-1 space-y-4 py-6">
            {/* Community Name */}
            <div className="space-y-2">
              <Label htmlFor="profileName">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="profileName"
                value={formData.profileName}
                onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                placeholder="Enter community name"
                maxLength={100}
                disabled={isSubmitting}
              />
              {validationErrors.profileName && (
                <p className="text-sm text-red-600">{validationErrors.profileName}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="profileDescription">
                Description
              </Label>
              <Textarea
                id="profileDescription"
                value={formData.profileDescription}
                onChange={(e) => setFormData({ ...formData, profileDescription: e.target.value })}
                placeholder="Enter description"
                maxLength={500}
                rows={3}
                disabled={isSubmitting}
              />
              {validationErrors.profileDescription && (
                <p className="text-sm text-red-600">{validationErrors.profileDescription}</p>
              )}
            </div>

            {/* Location Selection */}
            <div className="space-y-2">
              <Label htmlFor="actorId">
                Location <span className="text-red-500">*</span>
              </Label>
              <LocationSelector
                operatorId={selectedOperator?.operatorId || 0}
                selectedLocationId={formData.actorId}
                onLocationChange={(value) => {
                  // Find the selected actor and get its machineUsage
                  const selectedActor = actors.find(actor => actor.actorId.toString() === value);
                  let usageTypeValue = '1'; // Default to Public

                  if (selectedActor?.machineUsage) {
                    // Map MachineUsage enum to numeric values
                    const usageTypeMap: Record<string, string> = {
                      'Public': '1',
                      'SemiPublic': '2',
                      'PrivateHouse': '3',
                      'SharedBuilding': '4',
                    };
                    usageTypeValue = usageTypeMap[selectedActor.machineUsage] || '1';
                  }

                  setFormData({ ...formData, actorId: value || '', usageType: usageTypeValue });
                }}
                isActorsLoading={actorsLoading}
                autoSelectFirst={false}
                skipApiCallOnActorChange={false}
                className="w-full"
              />
              {validationErrors.actorId && (
                <p className="text-sm text-red-600">{validationErrors.actorId}</p>
              )}
            </div>

            {/* Discount Type */}
            <div className="space-y-2">
              <Label htmlFor="strategyType">
                Discount Type <span className="text-red-500">*</span>
              </Label>
              <Select
                key={`select-${currentCommunity?.profileId || 'new'}-${open}`}
                value={formData.strategyType}
                onValueChange={(value) => {
                  setFormData({ ...formData, strategyType: value });
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger id="strategyType">
                  <SelectValue placeholder="Select discount type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Energy Percentage</SelectItem>
                  <SelectItem value="1">Energy Amount (Per kWh)</SelectItem>
                  <SelectItem value="2">Total Percentage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Discount Value */}
            <div className="space-y-2">
              <Label htmlFor="singleDimensionValue">
                {getDiscountLabel()} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="singleDimensionValue"
                type="number"
                step="0.01"
                min="0"
                value={formData.singleDimensionValue}
                onChange={(e) =>
                  setFormData({ ...formData, singleDimensionValue: e.target.value })
                }
                placeholder="Enter discount value"
                disabled={isSubmitting}
                className={validationErrors.singleDimensionValue ? 'border-red-500' : ''}
              />
              {validationErrors.singleDimensionValue && (
                <p className="text-sm text-red-600">{validationErrors.singleDimensionValue}</p>
              )}
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label htmlFor="startDate">
                Start Date <span className="text-red-500">*</span>
              </Label>
              <DatePicker
                value={formData.startDate}
                onChange={(date) => setFormData({ ...formData, startDate: date })}
                placeholder="Select start date"
                disabled={isSubmitting}
              />
              {validationErrors.startDate && (
                <p className="text-sm text-red-600">{validationErrors.startDate}</p>
              )}
            </div>

            {/* Expiration Date */}
            {/* <div className="space-y-2">
              <Label htmlFor="expirationDate">Expiration Date</Label>
              <DatePicker
                value={formData.expirationDate}
                onChange={(date) => setFormData({ ...formData, expirationDate: date })}
                placeholder="Select expiration date"
                disabled={isSubmitting}
              />
              {validationErrors.expirationDate && (
                <p className="text-sm text-red-600">{validationErrors.expirationDate}</p>
              )}
              <p className="text-xs text-gray-500">Leave empty for no expiration</p>
            </div> */}

            {/* Status - Only shown in edit mode */}
            {isEditMode && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="profileStatus"
                    checked={formData.profileStatus}
                    onCheckedChange={(checked) => setFormData({ ...formData, profileStatus: checked as boolean })}
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="profileStatus" className="text-sm cursor-pointer">
                    Active
                  </Label>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}
          </div>

          <SheetFooter className="mt-6 pt-6 border-t flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-yellow-400 hover:bg-yellow-500 text-black">
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : isEditMode ? (
                'Update Community'
              ) : (
                'Create Community'
              )}
            </Button>
          </SheetFooter>
        </form>
          </TabsContent>

          {/* Accounts Tab Content */}
          <TabsContent value="accounts" className="flex-1 flex flex-col min-h-0 mt-0 pt-6">
            {isEditMode && currentCommunity ? (
              <CommunityAccountsTab communityId={currentCommunity.profileId} />
            ) : (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <p>Save the community first to manage accounts</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
